/**
 * パスワードハッシュのパラメータ（ARCHITECTURE.md §7.5 が唯一の正）。
 *
 * **実測検証済み**（Debian 12 bookworm / dovecot-core 2.3.19.1 / argon2@0.41.1、
 * 2026-08-05）:
 *   - `doveadm pw -l` に `ARGON2I ARGON2ID` が含まれる → イメージ差し替え不要。
 *   - node `argon2` npm の PHC 文字列（padding 無し b64）を
 *     `doveadm pw -t '{ARGON2ID}…'` が検証できる。誤ったパスワードは正しく拒否される。
 *   - Debian の dovecot-core は libsodium ではなく libargon2 にリンクしており
 *     `p=2` でも検証できる。`p=1` の採用は維持するが、根拠は
 *     「libsodium がシングルレーン専用だから必須」ではなく
 *     **「CP と全ノードでハッシュ検証結果を決定的にするため、および将来
 *       libsodium リンクのビルドへ差し替える余地を残すための保守的な選択」**。
 *
 * このモジュールは **argon2 を import しない**。SPA も `@mail-sv/shared` を
 * 読むため、ネイティブモジュールを引き込むとブラウザ用バンドルが壊れる。
 * 実際にハッシュを生成する `hashMailboxPassword()` ラッパは
 * `apps/api` / `apps/worker` 側に置き、必ずこの定数を使うこと
 * （argon2 npm の既定 parallelism は 1 ではないため、直接呼び出しは ESLint で禁止する）。
 */

/**
 * argon2id のパラメータ。**この値以外でメールボックスのパスワードを
 * ハッシュしてはならない。** CP が生成したハッシュをノードの Dovecot が
 * 検証するので、両者でパラメータの解釈が揃っている必要がある。
 */
export const ARGON2_PARAMS = {
  /** argon2 npm の `argon2.argon2id`（数値 2）に対応する。 */
  type: 'argon2id',
  /** 19 MiB。OWASP の推奨最小値。 */
  memoryCost: 19456,
  timeCost: 2,
  parallelism: 1,
  hashLength: 32,
  saltLength: 16,
} as const;

/**
 * Dovecot の passdb に載せるスキーム接頭辞。
 * `default_pass_scheme = CRYPT` にしてあるので、行頭の `{SCHEME}` が尊重される。
 * これにより方式の混在と段階移行ができる。
 */
export const MAILBOX_PASSWORD_SCHEME = 'ARGON2ID' as const;
export const MAILBOX_PASSWORD_PREFIX = `{${MAILBOX_PASSWORD_SCHEME}}` as const;

/** 期待する PHC 文字列。パラメータのドリフト検知に使う。 */
export const EXPECTED_ARGON2_PHC_PREFIX =
  `$argon2id$v=19$m=${ARGON2_PARAMS.memoryCost},t=${ARGON2_PARAMS.timeCost},p=${ARGON2_PARAMS.parallelism}$` as const;

/** argon2 npm が返す PHC 文字列に `{ARGON2ID}` を付ける。 */
export function toDovecotPasswordHash(phc: string): string {
  if (phc.startsWith(MAILBOX_PASSWORD_PREFIX)) return phc;
  if (!phc.startsWith('$argon2')) {
    throw new Error('argon2 の PHC 文字列ではありません');
  }
  return `${MAILBOX_PASSWORD_PREFIX}${phc}`;
}

/** `{SCHEME}` を剥がして生の PHC 文字列に戻す（検証時に使う）。 */
export function stripPasswordScheme(stored: string): { scheme: string | null; phc: string } {
  const m = /^\{([A-Z0-9.-]+)\}(.*)$/s.exec(stored);
  if (!m) return { scheme: null, phc: stored };
  return { scheme: m[1] ?? null, phc: m[2] ?? '' };
}

/**
 * 保存済みハッシュが現行パラメータで作られているか。
 * false のものは次回ログイン成功時に再ハッシュして移行する。
 */
export function needsPasswordRehash(stored: string): boolean {
  const { scheme, phc } = stripPasswordScheme(stored);
  if (scheme !== MAILBOX_PASSWORD_SCHEME) return true;
  return !phc.startsWith(EXPECTED_ARGON2_PHC_PREFIX);
}

/**
 * Dovecot の auth cache（`auth_cache_size = 32M` / `auth_cache_ttl = 1h`）は
 * argon2 の 19 MiB × 同時認証数のメモリ消費対策として**必須**。
 * その代償としてパスワード変更・アカウント停止の即時反映が要るので、
 * mailboxes セクションに変更があった世代でのみ shim の `authflush` を 1 回呼び、
 * 凍結など即時性が要る操作は `node_commands` の `auth.flush` を push する（§7.5）。
 */
export const DOVECOT_AUTH_CACHE = {
  size: '32M',
  ttl: '1h',
} as const;
